import { useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { useSetUrlEnabled } from './queries.js';

const ENABLED = 'enabled';
const DISABLED = 'disabled';

export type StatusMenuProps = {
  entryId: number;
  domainId: number;
  /** Sent back unchanged: the endpoint replaces the whole entry. */
  label: string;
  path: string;
  enabled: boolean;
  /** A viewer sees the badge and no menu — the flag is an admin's to set. */
  canEdit: boolean;
  /** What the badge says. Names the URL for a screen reader. */
  urlForLabel: string;
};

/**
 * The status badge, made into the control that sets the status.
 *
 * Reading and writing this flag were two different places before: the badge
 * said "Disabled", and turning it back on meant opening the edit dialog and
 * finding a checkbox. A menu on the badge itself collapses that into one
 * gesture, in the one place the operator is already looking.
 *
 * A menu of two rather than a switch, because the states have names worth
 * showing: a switch would have to be read together with the word beside it to
 * mean anything, and inside a table row that word is the badge it replaced.
 *
 * The change is applied immediately — there is no confirm step. It is
 * reversible from the same menu, and a URL nobody can re-enable in one click
 * is a worse failure than one disabled by a slip.
 */
export function StatusMenu({
  entryId,
  domainId,
  label,
  path,
  enabled,
  canEdit,
  urlForLabel,
}: StatusMenuProps) {
  const setEnabled = useSetUrlEnabled();
  const [open, setOpen] = useState(false);

  const badge = (
    <StatusBadge status={enabled ? 'active' : 'paused'} label={enabled ? 'Enabled' : 'Disabled'} />
  );

  if (!canEdit) return badge;

  return (
    <div className="inline-flex flex-col items-start gap-(--space-1)">
      <DropdownMenu
        open={open}
        onOpenChange={setOpen}
        /*
         * Not modal, deliberately.
         *
         * A modal Radix menu locks the document's scroll while it is open, and
         * removing the scrollbar re-centres every centred container on the page
         * — the whole layout jumps sideways by half the scrollbar's width as
         * the menu opens, and back as it closes. A dropdown is not a modal: the
         * page behind it stays legible and scrollable, and nothing moves.
         *
         * `scrollbar-gutter: stable` on the document does not save this case;
         * the lock is applied to `body`, so the reserved gutter on the scrolling
         * element is not what disappears.
         */
        modal={false}
      >
        <DropdownMenuTrigger
          /* A real button around the badge, so it is one tab stop with a name
             that says which URL it belongs to — inside a table of eight
             hundred rows, "Enabled" alone names nothing. */
          aria-label={`Status of ${urlForLabel}: ${enabled ? 'Enabled' : 'Disabled'}`}
          disabled={setEnabled.isPending}
          className="inline-flex items-center gap-(--space-1) rounded-(--radius-sm) text-ink-subtle transition-colors duration-(--duration-fast) hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-accent) disabled:opacity-70"
        >
          {badge}
          <ChevronDownIcon aria-hidden="true" className="size-3.5 shrink-0" />
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start">
          <DropdownMenuRadioGroup
            value={enabled ? ENABLED : DISABLED}
            onValueChange={(next) => {
              const wanted = next === ENABLED;
              if (wanted === enabled) return;
              setEnabled.mutate({
                entryId,
                domainId,
                label,
                path,
                enabled: wanted,
              });
            }}
          >
            <DropdownMenuRadioItem value={ENABLED}>Enabled</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value={DISABLED}>Disabled</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Inline rather than a toast: the row that failed is the row that must
          say so, and a table can have several. */}
      {setEnabled.isError && (
        <p role="alert" className="text-[length:var(--text-micro)] text-danger">
          {setEnabled.error instanceof Error
            ? setEnabled.error.message
            : 'Could not change the status'}
        </p>
      )}
    </div>
  );
}
